// S9b: the bar and the phrase, learned without labels.
//
// The dataset says where the odd counts are (the annotated beats) and nothing about the bar (counts 1 to 4) or the phrase (1 to 8). Given the odd beats, a song's fold over the
// 8-pulse cycle (four dancer beats, one bar) has two possible rotations: the bar starts on an odd beat of one parity of annotated-beat number or of the other. Over the 16-pulse
// cycle (eight beats, a phrase) it has two more, once the bar is known. There are no labels for either, so the consensus pattern is learned by alternating: give every song its
// better rotation under the current pattern, re-estimate the pattern from those, repeat; from several random starts. The answer is a pattern known up to one global rotation by
// half the cycle (the starts agree on which songs go together, not on which half is called first); `orientBar` and `orientClave` fix it from textbook parts and say how clear that is.
//
// Everything here is a function of folds (`fold.ts`), no audio.

import { PHASE_BANDS } from './bands.js';
import { logSoftmax } from './model.js';

const NB = PHASE_BANDS.length;

/** Pearson correlation of two equal-length vectors; 0 when either is flat. */
export function corr(a: ArrayLike<number>, b: ArrayLike<number>): number {
  const n = a.length;
  let ma = 0, mb = 0;
  for (let i = 0; i < n; i++) { ma += a[i]!; mb += b[i]!; }
  ma /= n; mb /= n;
  let sab = 0, saa = 0, sbb = 0;
  for (let i = 0; i < n; i++) { const x = a[i]! - ma, y = b[i]! - mb; sab += x * y; saa += x * x; sbb += y * y; }
  return saa > 1e-18 && sbb > 1e-18 ? sab / Math.sqrt(saa * sbb) : 0;
}

/** `p / scale` elementwise (a pattern in the units the consensus is learned and scored in). */
export function scaled(p: ArrayLike<number>, scale: ArrayLike<number> | null): Float64Array {
  return Float64Array.from(p as ArrayLike<number>, (x, i) => (scale ? x / scale[i]! : x));
}

/** Per-band scale of patterns: the root mean square of the band's numbers over every pattern given (so each band weighs the same in a correlation), repeated for each of the `L` positions. */
export function bandScale(patterns: readonly ArrayLike<number>[], L: number): Float64Array {
  const out = new Float64Array(NB * L).fill(1);
  for (let b = 0; b < NB; b++) {
    let n = 0, s = 0;
    for (const p of patterns) for (let k = 0; k < L; k++) { s += p[b * L + k]! ** 2; n++; }
    const sd = n ? Math.sqrt(s / n) : 0;
    for (let k = 0; k < L; k++) out[b * L + k] = sd > 1e-9 ? sd : 1;
  }
  return out;
}

export interface Consensus {
  /** The mean pattern of the songs' chosen rotations (in the scaled units). */
  readonly pattern: Float64Array;
  /** For each song, the index (into its candidates) of the rotation chosen. */
  readonly choice: readonly number[];
  /** Mean over songs of the correlation of the chosen rotation with the pattern: how well the songs agree with it. */
  readonly fit: number;
  readonly iterations: number;
}

function meanPattern(cands: readonly (readonly Float64Array[])[], choice: readonly number[]): Float64Array {
  const d = cands[0]![0]!.length;
  const m = new Float64Array(d);
  cands.forEach((c, i) => { const p = c[choice[i]!]!; for (let j = 0; j < d; j++) m[j]! += p[j]! / cands.length; });
  return m;
}

/**
 * Alternate between assigning each song its best candidate (highest correlation with the pattern) and re-estimating the pattern as the mean of the assigned candidates, from the
 * initial assignment `init`, until the assignment stops changing. A song's candidates are its fold under each possible rotation (already in the scaled units).
 */
export function alternate(cands: readonly (readonly Float64Array[])[], init: readonly number[], maxIter = 200): Consensus {
  let choice = [...init];
  let pattern = meanPattern(cands, choice);
  let it = 0;
  for (; it < maxIter; it++) {
    const next = cands.map((c) => { let best = 0, bc = -2; c.forEach((p, k) => { const r = corr(p, pattern); if (r > bc + 1e-12) { bc = r; best = k; } }); return best; });
    const same = next.every((x, i) => x === choice[i]);
    choice = next;
    pattern = meanPattern(cands, choice);
    if (same) break;
  }
  const fit = cands.reduce((a, c, i) => a + corr(c[choice[i]!]!, pattern), 0) / cands.length;
  return { pattern, choice, fit, iterations: it };
}

export interface ConsensusStarts {
  /** The run with the best fit. */
  readonly best: Consensus;
  readonly runs: readonly Consensus[];
  /** Per run, the share of songs that agree with the best run's grouping, up to the global flip (so never below 0.5 for two candidates). */
  readonly agreement: readonly number[];
}

/** Two-candidate groupings agree up to a global flip: the larger of the share equal and the share different. */
export function groupAgreement(a: readonly number[], b: readonly number[]): number {
  const same = a.reduce((s, x, i) => s + (x === b[i] ? 1 : 0), 0) / a.length;
  return Math.max(same, 1 - same);
}

/** `starts` runs of `alternate`, each from an independent random assignment (`rand` in [0, 1)); the best run by fit and the agreement of every run with it. Two candidates per song. */
export function consensusStarts(cands: readonly (readonly Float64Array[])[], starts: number, rand: () => number): ConsensusStarts {
  const runs: Consensus[] = [];
  for (let s = 0; s < starts; s++) runs.push(alternate(cands, cands.map(() => (rand() < 0.5 ? 0 : 1))));
  const best = runs.reduce((a, b) => (b.fit > a.fit + 1e-12 ? b : a));
  return { best, runs, agreement: runs.map((r) => groupAgreement(r.choice, best.choice)) };
}

/**
 * The same problem solved by its spectral relaxation. For a song with two candidates x and x', write x = a + b and x' = a - b (a: the part both share, b: the part the choice flips);
 * the best signs z_i maximise |sum z_i b_i|, whose relaxation is the leading axis u of the matrix sum b_i b_i^T, and z_i = sign(b_i . u). There are no local optima, and the eigenvalues
 * say whether one axis dominates (one consistent way the two candidates differ in most songs) or not. Returns the signs as candidate indices (0: b . u >= 0), the leading axis, and the
 * `top` eigenvalues' shares of the total.
 */
export function spectralGrouping(cands: readonly (readonly Float64Array[])[], top = 3): { readonly choice: readonly number[]; readonly axis: Float64Array; readonly shares: readonly number[] } {
  const d = cands[0]![0]!.length;
  const bs = cands.map((c) => Float64Array.from(c[0]!, (x, j) => (x - c[1]![j]!) / 2));
  const M = Array.from({ length: d }, () => new Float64Array(d));
  for (const b of bs) for (let i = 0; i < d; i++) for (let j = 0; j < d; j++) M[i]![j]! += b[i]! * b[j]!;
  let trace = 0;
  for (let i = 0; i < d; i++) trace += M[i]![i]!;
  const axes: Float64Array[] = [], values: number[] = [];
  for (let k = 0; k < top; k++) {
    let v = Float64Array.from({ length: d }, (_, i) => Math.sin(1 + i * 1.7 + k));
    let lambda = 0;
    for (let it = 0; it < 300; it++) {
      const w = new Float64Array(d);
      for (let i = 0; i < d; i++) { let s = 0; for (let j = 0; j < d; j++) s += M[i]![j]! * v[j]!; w[i] = s; }
      for (const a of axes) { let dot = 0; for (let i = 0; i < d; i++) dot += w[i]! * a[i]!; for (let i = 0; i < d; i++) w[i]! -= dot * a[i]!; }
      const n = Math.sqrt(w.reduce((a, x) => a + x * x, 0)) || 1;
      lambda = n;
      v = w.map((x) => x / n);
    }
    axes.push(v); values.push(lambda);
  }
  const axis = axes[0]!;
  const choice = bs.map((b) => (b.reduce((a, x, i) => a + x * axis[i]!, 0) >= 0 ? 0 : 1));
  return { choice, axis, shares: values.map((v) => (trace > 0 ? v / trace : 0)) };
}

/** Roll a band-major pattern of `L` positions per band by `k` positions: position j of the result is position (j + k) mod L of the input (the pattern as read from a cycle that starts k later). */
export function rollPattern(p: ArrayLike<number>, L: number, k: number): Float64Array {
  const bands = p.length / L;
  const out = new Float64Array(p.length);
  for (let b = 0; b < bands; b++) for (let j = 0; j < L; j++) out[b * L + j] = p[b * L + ((j + k) % L)]!;
  return out;
}

const mean = (xs: readonly number[]) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : 0);

// ---------------------------------------------------------------- orientation by textbook parts

/**
 * Bar positions (0 to 7) of the textbook parts, counting 1, 1&, 2, 2&, 3, 3&, 4, 4& as 0 to 7: the conga slap (bright and short: the high-mid band) on 2; its open
 * tones (the low-mid band) on 4 and 4&; the bass on 2& and 4. The same parts are in the Rhythm Lab (`LAB_PARTS`).
 */
export const BAR_PARTS: readonly { readonly name: string; readonly band: number; readonly positions: readonly number[] }[] = [
  { name: 'bass on 2& and 4', band: 0, positions: [3, 6] },
  { name: 'conga open tones (low-mid) on 4 and 4&', band: 1, positions: [6, 7] },
  { name: 'conga slap (high-mid) on 2', band: 2, positions: [2] },
];

export interface Orientation {
  /** Roll to apply so that position 0 of the pattern is count 1 (0 or 4 in the 8-cycle): `rollPattern(pattern, 8, roll)`. */
  readonly roll: 0 | 4;
  /** Per part: the mean modulation at the part's positions if position 0 is count 1, minus the same if the pattern starts 4 positions later (positive: it is count 1), and the roll it votes for. */
  readonly parts: readonly { readonly name: string; readonly contrast: number; readonly votes: 0 | 4 }[];
  /** The sum of the parts' contrasts (positive favours roll 0). */
  readonly total: number;
  /** All the parts vote the same way. */
  readonly unanimous: boolean;
  /** `clear`: unanimous, the total at least `minTotal` and every part's contrast at least `minContrast`; `weak`: the total is at least `minTotal` but not all parts are clear; `unoriented`: the total is under `minTotal` (no textbook asymmetry). */
  readonly verdict: 'clear' | 'weak' | 'unoriented';
}

/**
 * Which rotation of a learned 8-position pattern puts the textbook parts where they belong (`BAR_PARTS`). A part's contrast is its band's modulation at its positions if position 0 is
 * count 1, minus the same if position 0 is count 3; the roll is the sign of their sum. `pattern` is band-major (4 bands x 8), in the same scaling for every band.
 */
export function orientBar(pattern: ArrayLike<number>, minContrast = 0.05, minTotal = 0.1): Orientation {
  const at = (b: number, k: number) => pattern[b * 8 + (((k % 8) + 8) % 8)]!;
  const parts = BAR_PARTS.map((part) => {
    const contrast = mean(part.positions.map((k) => at(part.band, k))) - mean(part.positions.map((k) => at(part.band, k + 4)));
    return { name: part.name, contrast, votes: (contrast >= 0 ? 0 : 4) as 0 | 4 };
  });
  const total = parts.reduce((a, p) => a + p.contrast, 0);
  const unanimous = parts.every((p) => p.votes === parts[0]!.votes);
  const verdict = Math.abs(total) < minTotal ? 'unoriented' : unanimous && parts.every((p) => Math.abs(p.contrast) >= minContrast) ? 'clear' : 'weak';
  return { roll: total >= 0 ? 0 : 4, parts, total, unanimous, verdict };
}

/**
 * Positions (within a bar, 0 to 7) of the clave strokes on the three-side and the two-side: 3-2 is 1, 2&, 4 | 6, 7; 2-3 is 2, 3 | 5, 6&, 8. In either, the bar that holds
 * the three-side has strokes at positions 0, 3, 6 and the bar that holds the two-side at 2, 4.
 */
export const THREE_SIDE: readonly number[] = [0, 3, 6];
export const TWO_SIDE: readonly number[] = [2, 4];

export interface ClaveOrientation {
  /** Roll to apply to the 16-pattern so that its first bar (positions 0 to 7) is the bar with the three-side: 0 or 8. */
  readonly roll: 0 | 8;
  /** Per band: the modulation at the strokes if the first bar holds the three-side, minus if it holds the two-side (positive: the first bar holds the three-side). */
  readonly bands: readonly { readonly name: string; readonly contrast: number }[];
  readonly total: number;
  readonly verdict: 'clear' | 'weak' | 'unoriented';
}

/**
 * Which half of a learned 16-position pattern is the three-side's bar, from the clave strokes alone: the pattern at THREE_SIDE of the first bar and TWO_SIDE of the second, against
 * THREE_SIDE of the second and TWO_SIDE of the first. The clave is the one part of a rhythm section that differs between the two bars of a phrase in every salsa; `bands` says in which bands the
 * contrast shows (the clave is a woody tone at about 2.5 kHz: the high-mid band). `useBands` selects the bands summed (default: high-mid and top).
 */
export function orientClave(pattern: ArrayLike<number>, useBands: readonly number[] = [2, 3], minTotal = 0.1): ClaveOrientation {
  const at = (b: number, k: number) => pattern[b * 16 + (((k % 16) + 16) % 16)]!;
  const bands = PHASE_BANDS.map((band, b) => {
    const asIs = mean(THREE_SIDE.map((k) => at(b, k))) + mean(TWO_SIDE.map((k) => at(b, 8 + k)));
    const flipped = mean(THREE_SIDE.map((k) => at(b, 8 + k))) + mean(TWO_SIDE.map((k) => at(b, k)));
    return { name: band.name, contrast: asIs - flipped };
  });
  const total = useBands.reduce((a, b) => a + bands[b]!.contrast, 0);
  const verdict = Math.abs(total) < minTotal ? 'unoriented' : useBands.every((b) => Math.sign(bands[b]!.contrast) === Math.sign(total)) ? 'clear' : 'weak';
  return { roll: total >= 0 ? 0 : 8, bands, total, verdict };
}

// ---------------------------------------------------------------- scoring a song against a consensus

/**
 * Log-probabilities of a song's candidate rotations under a consensus: softmax of `kappa` times the correlation of each candidate pattern (scaled like the consensus) with it.
 * `kappa` is a temperature (calibrated by the bench).
 */
export function candidateLogProbs(consensus: ArrayLike<number>, candidates: readonly Float64Array[], kappa: number): number[] {
  return logSoftmax(candidates.map((p) => kappa * corr(p, consensus)));
}
